import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { Dispatch, FC, Fragment, SetStateAction, useCallback, useMemo } from 'react'
import { exportTypeToEditedVisualsType, exportTypeToEditedVisualsWebType } from 'constants/visualEditing'
import { ExportOption, VisualEditingSteps, patternList } from 'constants/misc'
import { handleLoadEditedVisuals } from 'utils/visuals'

import { LoadEditedVisualResult } from 'models/visualsEditing'
import { logger } from 'utils/logging'
import Button from 'components/common/Button/Button'
import { WorkspaceRole } from 'models/user'
import styles from 'components/pages/VisualsEditing/VisualEditingFooter/VisualEditingFooter.module.sass'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'
import { useVisualsEditing } from '../VisualsEditing.context'
import { useVisualsEditingDownload } from '../VisualsEditingDownload.context'
import { useWorkspacePlan } from 'utils/hooks'
import { useWorkspacesProfileContext } from 'components/pages/Client/WorkspacesProfile/_main/WorkspacesProfile.context'

/**
 * @interface Props Input properties
 */
interface Props {
  /** SetState action control EditProcessingPopup */
  setEditProcessingPopup: Dispatch<SetStateAction<boolean>>
  /** SetState action control SaveTemplatePopup */
  setSaveTemplatePopup: Dispatch<SetStateAction<boolean>>
  /** SetState action control DownloadPopup */
  setDownloadPopup: Dispatch<SetStateAction<boolean>>
}

/**
 * @component VisualsEditingFooter
 * @example
 * <VisualsEditingFooter />
 */
export const VisualsEditingFooter: FC<Props> = ({
  setEditProcessingPopup,
  setSaveTemplatePopup,
  setDownloadPopup
}) => {
  const { t } = useTranslation(['visuals_editing'])

  const { workspaceDataOfUser } = useWorkspacesProfileContext()
  const { currentUserWorkspace, clientData } = useUserData()
  const { archiveEditingVisualsMutation } = useVisualsEditingDownload()
  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()

  const {
    assignmentId,
    editingJobId,
    processPreviewDisabled,
    logEditEvent,
    logSubmitEdit,
    activeFlowStep,
    setActiveFlowStep,
    isWatermarkActive,
    uploadedWatermarkLogo,
    visualEditingFilenames,
    selectedWatermarkSize,
    selectedWatermarkOpacity,
    selectedWatermarkPosition,
    selectedRatioPattern,
    exportOption,
    isDownloadRunning,
    saveTemplateStatusSuccess,
    triggerVisualEditing,
    jobPoll,
    currentWorkspaceData,
    sendEditedVisualsNoSubscription,
    isDownloadReady,
    setLoadedEditedVisuals,
  } = useVisualsEditing()

  /** Updates loadedEditedVisuals state with worker result, using reducer-style nesting */
  const onWorkerResult = useCallback((action: LoadEditedVisualResult) => {
    const { jobId, filename, visualType, signedUrl, request } = action
    setLoadedEditedVisuals(prev => ({
      ...prev,
      [jobId]: {
        ...prev[jobId],
        [filename]: {
          ...prev[jobId]?.[filename],
          [visualType]: {
            filename,
            signedUrl: signedUrl || '',
            request,
          },
        },
      },
    }))
  }, [setLoadedEditedVisuals])

  const onGoBackToEditing = useCallback(() => {
    logEditEvent(AnalyticsEvent.CANCEL_DOWNLOAD_BULK_EDITING)
    setActiveFlowStep(VisualEditingSteps.SETTINGS)
    setLoadedEditedVisuals({})
    archiveEditingVisualsMutation.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setActiveFlowStep, logEditEvent, setLoadedEditedVisuals])

  const isWorkspaceAdmin = workspaceDataOfUser?.role === WorkspaceRole.ADMIN

  const submitError = useMemo(() => {
    // The Active Watermark section is missing a logo, either completely or due to user deletion.
    if (isWatermarkActive && !uploadedWatermarkLogo?.signedURL) return t('error_upload_logo')
    // The user hasn't made any changes to the visuals.
    if (processPreviewDisabled) return t('error_select_options')
    return null
  }, [isWatermarkActive, processPreviewDisabled, t, uploadedWatermarkLogo])

  /** Dispatches the archive editing visuals mutation for a given jobId based on the export option. */
  const handleVisualsExportDispatch = useCallback(async (jobId: string) => {
    const types = exportOption === ExportOption.WEB
      ? [exportTypeToEditedVisualsWebType[exportOption]]
      : [exportTypeToEditedVisualsType[exportOption]]

    await archiveEditingVisualsMutation.mutateAsync({
      assignmentId,
      exportId: jobId,
      types,
      filenames: visualEditingFilenames,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId, exportOption, visualEditingFilenames])

  const handlePreviewAndArchiveVisuals = useCallback(async (jobId: string) => {
    try {
      await jobPoll.mutateAsync({ jobId })

      await Promise.all([
        handleLoadEditedVisuals(jobId, assignmentId, visualEditingFilenames, exportTypeToEditedVisualsType[exportOption], onWorkerResult),
        handleLoadEditedVisuals(jobId, assignmentId, visualEditingFilenames, exportTypeToEditedVisualsWebType[exportOption], onWorkerResult),
      ])
    } catch (e) {
      return logger.error('Failed to load edited visual previews', e as Error, { context: { component: 'VisualsEditingFooter', action: 'handlePreviewAndArchiveVisuals' } })
    }
  }, [assignmentId, onWorkerResult, exportOption, jobPoll, visualEditingFilenames])

  const onEditVisuals = useCallback(() => {
    if (!currentUserWorkspace) return

    triggerVisualEditing.mutate({
      filenames: visualEditingFilenames,
      assignmentId,
      applyWatermark: isWatermarkActive,
      watermarkSize: selectedWatermarkSize,
      watermarkOpacity: selectedWatermarkOpacity,
      watermarkPositioning: selectedWatermarkPosition,
      imageRatio: selectedRatioPattern.value,
      exportOption,
      watermarkId: currentWorkspaceData?.watermarks[0]?.id || '',
      workspaceId: currentUserWorkspace.id
    }, {
      onSuccess: (response) => {
        const jobId = response.data.id
        handlePreviewAndArchiveVisuals(jobId)
      }
    })

    logSubmitEdit()
    setEditProcessingPopup(true)
  }, [assignmentId, currentUserWorkspace, currentWorkspaceData?.watermarks, exportOption, handlePreviewAndArchiveVisuals, isWatermarkActive, logSubmitEdit, selectedRatioPattern.value, selectedWatermarkOpacity, selectedWatermarkPosition, selectedWatermarkSize, setEditProcessingPopup, triggerVisualEditing, visualEditingFilenames])

  const handleProcessPreview = useCallback(() => {
    if (submitError) return
    onEditVisuals()
  }, [onEditVisuals, submitError])

  const handleBasicDownload = useCallback(async () => {
    setDownloadPopup(true)

    sendEditedVisualsNoSubscription.mutate({
      filenames: visualEditingFilenames,
      assignmentId,
      applyWatermark: false, // do not send watermarkId because user doesn't have subscription and we don't want to apply watermark
      imageRatio: patternList[0].value, // Use default, ORIGINAL
      exportOption,
      format: exportOption,
    }, {
      onSuccess: async (response) => {
        const jobId = response.data.id
        try {
          await jobPoll.mutateAsync({ jobId })

          await handleVisualsExportDispatch(jobId)
        } catch (e) {
          return logger.error('Failed to export visuals', e as Error, { context: { component: 'VisualsEditingFooter', action: 'handleBasicDownload' } })
        }
      }
    })
  }, [assignmentId, exportOption, handleVisualsExportDispatch, jobPoll, sendEditedVisualsNoSubscription, setDownloadPopup, visualEditingFilenames])

  return (
    <div className={styles.footerContent}>

      {activeFlowStep === VisualEditingSteps.SETTINGS &&
        <Fragment>

          <span className={styles.processPreviewError}>{submitError}</span>

          {hasWorkspaceActiveSubscription
            ? (
              <Button type="primary" disabled={!!submitError} onClick={handleProcessPreview}>
                {t('preview_visuals')}
              </Button>
            )
            : (
              <Button
                type="primary"
                disabled={!!submitError || (sendEditedVisualsNoSubscription.isSuccess && isDownloadRunning)}
                isLoading={sendEditedVisualsNoSubscription.isSuccess && !isDownloadReady}
                onClick={handleBasicDownload}
              >
                {t('export_visuals')}
              </Button>
            )
          }

        </Fragment>
      }

      {activeFlowStep === VisualEditingSteps.PREVIEW &&
        <Fragment>

          <Button type="secondary noborder" onClick={onGoBackToEditing} className={styles.actionButton}>
            {t('go_back_to_editing')}
          </Button>

          {isWorkspaceAdmin &&
            <Button
              type="secondary"
              className={styles.actionButton}
              disabled={saveTemplateStatusSuccess}
              onClick={() => {
                logAnalyticsEvent(AnalyticsEvent.CLICK_SAVE_TEMPLATE, {
                  userEmail: clientData?.email,
                  organizationId: clientData?.organizationId,
                  logoSetting: isWatermarkActive ? `${selectedWatermarkSize},${selectedWatermarkOpacity},${selectedWatermarkPosition}` : '',
                  ratioSetting: selectedRatioPattern.value,
                  exportSetting: exportOption,
                })
                setSaveTemplatePopup(true)
              }}
            >
              {t(saveTemplateStatusSuccess ? 'save_template.template_saved' : 'save_template.save_settings_as_template')}
            </Button>
          }

          <Button
            type="primary"
            disabled={isDownloadRunning}
            onClick={async () => {
              logEditEvent('Download Bulk Editing', { numberOfVisuals: visualEditingFilenames.length })
              setDownloadPopup(true)
              try {
                await handleVisualsExportDispatch(editingJobId)
              } catch (e) {
                logger.error('Failed to export visuals for download', e as Error, {
                  context: { component: 'VisualsEditingFooter', action: 'downloadClick' }
                })
              }
            }}
          >
            {t('export_visuals')}
          </Button>

        </Fragment>
      }

    </div>
  )
}
